import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Paper, Box, Skeleton, Grid2 as Grid } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import Select from '@/components/Common/Select';
import Input from '@/components/Common/Input';
import Button from '@/components/Common/Button';
import {
  useGetPublicAvailableIntegrations,
  useGetOnboardingPostConfigurationFields,
  useValidateIntegrationCredentials,
  useCreateOrUpdateAgencyOnboardingForm,
  useUpdatePublicOnboardingForm
} from '@/apis/agency/businessOnboarding/hooks';
import { OnboardingFormRequest } from '@/apis/agency/businessOnboarding/types';
import DocumentationDrawer from '@/components/Common/DocumentationDrawer';
import { titleCase } from '@/utils/helperFunctions';
import FormSchemaParser from '@/components/Common/FormSchemaParser';
import DocumentationIcon from '@/assets/icons/DocumentationIcon';
import { useGetOrganizations } from '@/apis/agency/organizations/hook';
import { PiFileCodeDuotone } from 'react-icons/pi';
import { FaRegEdit } from 'react-icons/fa';
import ConfigurationLoaderBadge from '@/components/Common/ConfigurationLoaderBadge';
import IntegrationPostConfigurationFields from '@/pages/Business/Setting/Integrations/Components/IntegrationPostConfigurationFields';
import { getIntegrationCredentials, normalizeIntegrationConfiguration } from '../components/utils';

const IntegrationDetailForm = ({ activeStep }: { activeStep?: number }) => {
  const location = useLocation();
  const isPublicRoute = location.pathname.includes('/business-onboarding/stage-2');
  const onboardingId = Cookies.get('onboarding_id');
  const { data: organizationData } = useGetOrganizations();
  const { control, watch, setValue, getValues } = useFormContext();
  const [docsDrawerOpen, setDocsDrawerOpen] = useState(false);
  const [credentialBadgeStatus, setCredentialBadgeStatus] = useState<null | 'pending' | 'success' | 'failed'>(null);
  const [credentialBadgeHeading, setCredentialBadgeHeading] = useState<string | undefined>();
  const [credentialBadgeSubHeading, setCredentialBadgeSubHeading] = useState<string | undefined>();
  const [editing, setEditing] = useState(true);
  const [postConfigCredentials, setPostConfigCredentials] = useState<Record<string, unknown> | null>(null);
  const [postConfigFetchKey, setPostConfigFetchKey] = useState(0);
  const selectedIndustry = watch('industry');
  const integrationConfiguration = watch('integration_configuration');
  const selectedIntegrationId = watch('available_integration_ids[0]');

  const credentialsValid = integrationConfiguration?.valid_creds === true || integrationConfiguration?.valid === true;

  const { data: availableIntegrations, isLoading: isIntegrationsLoading } = useGetPublicAvailableIntegrations({
    per_page: 999,
    filter: { industries_contain: selectedIndustry }
  });
  const { mutateAsync: validateCredentials, isPending: isValidating } = useValidateIntegrationCredentials();
  const { mutate: updateAgencyOnboardingForm } = useCreateOrUpdateAgencyOnboardingForm(false);
  const { mutate: updatePublicOnboardingForm } = useUpdatePublicOnboardingForm();

  const prevIntegrationIdRef = useRef(selectedIntegrationId);
  const hasInitializedValidStateRef = useRef(false);
  const lastPersistedConfigurationRef = useRef<string>('');
  const badgeDismissedRef = useRef(false);

  const persistIntegrationStep = useCallback(
    (configuration: Record<string, unknown>, updateMessage: string, options?: { skipSuccessToast?: boolean }) => {
      const formData: any = { ...getValues(), integration_configuration: configuration };
      const integrationId = formData.available_integration_ids?.[0];

      if (integrationId === ' ' || integrationId === 'other') {
        formData.available_integration_name = integrationId === ' ' ? 'No Integration' : 'other';
        formData.available_integration_ids = [];
      }

      if (formData.status === '') {
        delete formData.status;
      }

      const step = activeStep ?? 0;
      const payload: OnboardingFormRequest = {
        onboarding_form: {
          ...formData,
          integration_configuration: configuration,
          ...(isPublicRoute
            ? {
                onboarding_config: {
                  ...(formData.onboarding_config || {}),
                  step: String(step + 1),
                  stage: '2',
                  overall_progress: formData.onboarding_config?.overall_progress
                }
              }
            : {
                step_config: {
                  ...(formData.step_config || {}),
                  current_step: step,
                  overall_progress: formData.step_config?.overall_progress
                }
              })
        },
        update_message: updateMessage
      };

      if (isPublicRoute) {
        if (onboardingId) {
          updatePublicOnboardingForm({
            formId: onboardingId,
            payload,
            skipSuccessToast: options?.skipSuccessToast
          });
        }
      } else {
        updateAgencyOnboardingForm({
          payload,
          skipSuccessToast: options?.skipSuccessToast
        });
      }
    },
    [activeStep, getValues, isPublicRoute, onboardingId, updateAgencyOnboardingForm, updatePublicOnboardingForm]
  );

  useEffect(() => {
    if (prevIntegrationIdRef.current !== selectedIntegrationId) {
      setValue('integration_configuration', {});
      setCredentialBadgeStatus(null);
      setCredentialBadgeHeading(undefined);
      setCredentialBadgeSubHeading(undefined);
      setEditing(true);
      setPostConfigCredentials(null);
      setPostConfigFetchKey(0);
      lastPersistedConfigurationRef.current = '';
      hasInitializedValidStateRef.current = false;
      badgeDismissedRef.current = false;
      prevIntegrationIdRef.current = selectedIntegrationId;
    }
  }, [selectedIntegrationId, setValue]);

  useEffect(() => {
    if (badgeDismissedRef.current && integrationConfiguration?.valid !== false) return;

    if (integrationConfiguration?.valid === false) {
      badgeDismissedRef.current = false;
      setCredentialBadgeStatus('failed');
      setCredentialBadgeHeading(undefined);
      setCredentialBadgeSubHeading(
        typeof integrationConfiguration?.validation_message === 'string'
          ? integrationConfiguration.validation_message
          : undefined
      );
      return;
    }

    if (hasInitializedValidStateRef.current || !credentialsValid) return;

    if (badgeDismissedRef.current) return;

    setEditing(false);
    setCredentialBadgeStatus((prev) => prev ?? 'success');
    setCredentialBadgeHeading(undefined);
    setCredentialBadgeSubHeading(undefined);
    hasInitializedValidStateRef.current = true;
  }, [credentialsValid, integrationConfiguration?.valid, integrationConfiguration?.validation_message]);

  const ehrSystems = availableIntegrations?.data || [];
  const selectedIntegration = ehrSystems.find(
    (integration: any) => String(integration.id) === String(selectedIntegrationId)
  );

  const postConfigurationRequired = Boolean(selectedIntegration?.post_configuration_required);
  const selectedIntegrationCode = selectedIntegration?.code as string | undefined;
  const shouldFetchPostConfig = Boolean(
    selectedIntegrationCode && postConfigurationRequired && credentialsValid && editing && postConfigCredentials != null
  );

  const { data: postConfigurationFields, isLoading: fetchingPostConfigFields } =
    useGetOnboardingPostConfigurationFields(
      selectedIntegrationCode,
      postConfigCredentials ?? {},
      shouldFetchPostConfig,
      isPublicRoute,
      postConfigFetchKey
    );

  const memorizedPostConfigFields = useMemo(() => {
    return postConfigurationFields || integrationConfiguration?.post_configurations;
  }, [postConfigurationFields, integrationConfiguration?.post_configurations]);

  useEffect(() => {
    if (postConfigurationFields) {
      setValue('integration_configuration.post_configurations', postConfigurationFields);
    }
  }, [postConfigurationFields, setValue]);

  // Persist pre+post config values under integration_configuration.configuration (same shape as validate)
  useEffect(() => {
    if (!credentialsValid || !editing || !postConfigurationRequired || postConfigCredentials == null) return;
    if (fetchingPostConfigFields) return;

    const timeoutId = window.setTimeout(() => {
      const current = (getValues('integration_configuration') || {}) as Record<string, unknown>;
      const configuration = getIntegrationCredentials(current);
      const serialized = JSON.stringify(configuration);
      if (serialized === lastPersistedConfigurationRef.current) return;

      const normalized = normalizeIntegrationConfiguration(current, {
        configuration,
        valid: true,
        valid_creds: true
      });
      lastPersistedConfigurationRef.current = serialized;
      setValue('integration_configuration', normalized, { shouldDirty: true });
      persistIntegrationStep(normalized, 'Integration configuration updated', { skipSuccessToast: true });
    }, 600);

    return () => window.clearTimeout(timeoutId);
  }, [
    integrationConfiguration?.configuration,
    credentialsValid,
    editing,
    postConfigurationRequired,
    postConfigCredentials,
    fetchingPostConfigFields,
    getValues,
    setValue,
    persistIntegrationStep
  ]);

  const formValues = watch();
  const isOAuthIntegration = selectedIntegration?.connection_type === 'oauth';
  const hasPreConfigurations = (selectedIntegration?.pre_configurations?.length ?? 0) > 0;
  const showCredentialsSection =
    hasPreConfigurations ||
    (isOAuthIntegration && selectedIntegration && selectedIntegrationId !== 'other' && selectedIntegrationId !== ' ');

  const credentialsDisabled = credentialsValid;

  const hasRequiredCredentials = useMemo(() => {
    const configuration = integrationConfiguration.configuration || {};
    return (
      selectedIntegration?.pre_configurations
        ?.filter((field: any) => field.required)
        .every((field: any) => {
          const value = configuration[field.key];
          if (field.type === 'boolean') return value != null;
          if (field.multiple) return Array.isArray(value) && value.length > 0;
          return value != null && value !== '';
        }) ?? true
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationConfiguration, selectedIntegration?.pre_configurations, formValues]);

  const canValidateCredentials =
    Boolean(selectedIntegration?.integration_name) &&
    (isOAuthIntegration ? !hasPreConfigurations || hasRequiredCredentials : hasRequiredCredentials);

  const validateButtonLabel = isOAuthIntegration ? 'Validate OAuth Credentials' : 'Validate Credentials';

  const articleId = selectedIntegration?.article_id ?? null;

  const runCredentialValidation = async (rawConfiguration: Record<string, unknown>, redirectUri?: string) => {
    if (!selectedIntegration?.integration_name) return;

    const credentials = getIntegrationCredentials(rawConfiguration);
    const credentialsForValidation = {
      ...credentials,
      ...(typeof redirectUri === 'string' && redirectUri.length > 0 ? { redirect_uri: redirectUri } : {})
    };

    const response = await validateCredentials({
      integration: {
        integration_name: selectedIntegration.integration_name,
        configuration: credentialsForValidation
      }
    });

    if (response?.requires_oauth && response?.oauth_url) {
      window.location.href = response.oauth_url;
      return;
    }

    const isSuccess = Boolean(response?.success && response?.active);
    const configurationWithValidity = normalizeIntegrationConfiguration(rawConfiguration, {
      configuration: credentials,
      redirect_uri: redirectUri,
      valid: isSuccess,
      valid_creds: isSuccess,
      validation_message: isSuccess
        ? null
        : response?.message || 'Integration authentication failed. Please check your credentials and update.'
    });
    lastPersistedConfigurationRef.current = JSON.stringify(credentials);
    setValue('integration_configuration', configurationWithValidity, { shouldDirty: true });
    setCredentialBadgeStatus(isSuccess ? 'success' : 'failed');

    if (isSuccess) {
      if (postConfigurationRequired && !response?.manual_validation_required) {
        setPostConfigCredentials(structuredClone(credentials));
        setPostConfigFetchKey((k) => k + 1);
        setEditing(true);
        hasInitializedValidStateRef.current = true;
      } else {
        setEditing(false);
      }
    }

    persistIntegrationStep(
      configurationWithValidity,
      isSuccess ? 'Integration credentials validated successfully' : 'Integration credentials validation failed',
      { skipSuccessToast: true }
    );

    if (isSuccess && response?.manual_validation_required && response?.message) {
      setCredentialBadgeHeading('Credentials Submitted for Review');
      setCredentialBadgeSubHeading(response.message);
      return;
    }

    setCredentialBadgeHeading(undefined);
    setCredentialBadgeSubHeading(response?.message || undefined);
  };

  const handleValidateCredentials = async () => {
    if (!selectedIntegration?.integration_name) return;

    setCredentialBadgeStatus('pending');
    const stepQuery = activeStep != null ? `?onboarding_step=${activeStep + 1}` : '';
    const redirectUri = `${window.location.origin}${window.location.pathname}${stepQuery}`;
    const currentConfiguration = (integrationConfiguration || {}) as Record<string, unknown>;

    try {
      await runCredentialValidation(currentConfiguration, redirectUri);
    } catch {
      const failureMessage = 'Integration authentication failed. Please check your credentials and update.';
      const configurationWithValidity = normalizeIntegrationConfiguration(currentConfiguration, {
        redirect_uri: redirectUri,
        valid: false,
        valid_creds: false,
        validation_message: failureMessage
      });
      setValue('integration_configuration', configurationWithValidity, { shouldDirty: true });
      setCredentialBadgeStatus('failed');
      setCredentialBadgeSubHeading(failureMessage);
      persistIntegrationStep(configurationWithValidity, 'Integration credentials validation failed', {
        skipSuccessToast: true
      });
    }
  };

  const showPostConfigSection =
    postConfigurationRequired &&
    credentialsValid &&
    (memorizedPostConfigFields || (editing && fetchingPostConfigFields));

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #EAEAEA',
        borderRadius: 2,
        p: { xs: 2, md: 4 }
      }}
    >
      <Typography fontWeight={600} fontSize={18} mb={0.5}>
        Integration Details
      </Typography>

      <Typography variant='body2' color='text.secondary' mb={3}>
        Select your integration.
      </Typography>

      {credentialBadgeStatus && (
        <ConfigurationLoaderBadge
          status={credentialBadgeStatus}
          heading={credentialBadgeHeading}
          subHeading={credentialBadgeSubHeading}
          helperText="You can validate later this won't block you from moving to the next step."
          onClose={() => {
            badgeDismissedRef.current = true;
            setCredentialBadgeStatus(null);
            setCredentialBadgeHeading(undefined);
            setCredentialBadgeSubHeading(undefined);
          }}
        />
      )}

      <Grid container spacing={3}>
        <Grid size={12}>
          {isIntegrationsLoading ? (
            <Box width='100%'>
              <Skeleton variant='text' width={120} height={20} sx={{ mb: 0.5 }} />
              <Skeleton variant='rounded' height={56} width='100%' />
            </Box>
          ) : (
            <Select
              name='available_integration_ids[0]'
              label='Integration'
              disabled={Boolean(selectedIntegration?.code)}
              control={control}
              sortOrder='none'
              fullWidth
              required
              options={[
                { label: 'No Integration', value: ' ' },
                ...ehrSystems.map((system) => ({
                  label: system.name,
                  value: system.id
                })),
                { label: 'Other', value: 'other' }
              ]}
            />
          )}
        </Grid>

        {watch('available_integration_ids[0]') === 'other' && (
          <>
            <Grid size={12}>
              <Input
                name='other_integration_name'
                label='Integration Name'
                control={control}
                fullWidth
                rules={{ required: 'Integration name is required' }}
              />
            </Grid>
            <Grid size={12}>
              <Input
                name='integration_configuration.other_credential'
                label='Credentials'
                control={control}
                fullWidth
                multiline
                rows={3}
                rules={{ required: 'Credentials are required' }}
              />
            </Grid>
          </>
        )}
      </Grid>

      {showCredentialsSection ? (
        <Box border='1px solid #ECECEC' mt={5} p={3} borderRadius={2}>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={2}
            mb={hasPreConfigurations ? 2 : 0}
          >
            <Typography fontSize={16} fontWeight={600}>
              {titleCase(selectedIntegration?.name || 'Integration')} Credentials
            </Typography>
            <Box display='flex' gap={1} flexWrap='wrap' flexShrink={0}>
              {organizationData?.uid === 'AG316997' && (
                <Button
                  variant='outlined'
                  disabled={!articleId}
                  onClick={() => setDocsDrawerOpen(true)}
                  sx={{
                    borderRadius: '999px',
                    textTransform: 'none',
                    fontWeight: 500,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    bgcolor: '#fff',
                    gap: 0.75,
                    px: 2,
                    py: 0.75,
                    '& .MuiButton-startIcon': { margin: 0 },
                    '&:hover': { bgcolor: 'primary.light', borderColor: 'primary.main' }
                  }}
                >
                  <Box component='span' sx={{ display: 'flex', '& svg': { width: 20, height: 20 } }}>
                    <DocumentationIcon />
                  </Box>
                  View Documentation
                </Button>
              )}
              {credentialsValid && !editing ? (
                <Button
                  variant='contained'
                  startIcon={<FaRegEdit />}
                  onClick={() => {
                    const credentials = getIntegrationCredentials(getValues('integration_configuration') || {});
                    setPostConfigCredentials(structuredClone(credentials));
                    setPostConfigFetchKey((key) => key + 1);
                    setEditing(true);
                  }}
                >
                  Edit
                </Button>
              ) : credentialsValid ? (
                ''
              ) : (
                <Button
                  variant='contained'
                  startIcon={<PiFileCodeDuotone />}
                  disabled={!canValidateCredentials || isValidating}
                  isPending={isValidating}
                  onClick={handleValidateCredentials}
                >
                  {validateButtonLabel}
                </Button>
              )}
            </Box>
          </Box>

          {hasPreConfigurations && (
            <FormSchemaParser
              publicForm={true}
              fields={selectedIntegration?.pre_configurations}
              control={control}
              namePrefix='integration_configuration.configuration'
              gridSize={12}
              spacing='16px'
              disabled={credentialsDisabled}
            />
          )}

          {isOAuthIntegration && !hasPreConfigurations && (
            <Typography variant='body2' color='text.secondary' mt={2}>
              {selectedIntegration?.name} requires OAuth authentication. Click validate to connect your account.
            </Typography>
          )}
        </Box>
      ) : (
        !isIntegrationsLoading &&
        selectedIntegrationId !== 'other' &&
        selectedIntegrationId !== ' ' && (
          <Box
            sx={{ p: 4, border: '1px dashed #ccc', borderRadius: 2, textAlign: 'center', bgcolor: '#f9f9f9', mt: 3 }}
          >
            <Typography variant='body2' color='text.secondary'>
              No additional credentials required for the selected integration.
            </Typography>
          </Box>
        )
      )}

      {showPostConfigSection && (
        <Box border='1px solid #ECECEC' mt={3} p={3} borderRadius={2}>
          <Typography fontSize={16} fontWeight={600} mb={2}>
            Configuration
          </Typography>
          <IntegrationPostConfigurationFields
            selectedCode={selectedIntegration?.code}
            integrationId={String(selectedIntegrationId)}
            configFields={memorizedPostConfigFields}
            namePrefix='integration_configuration.configuration'
            control={control}
            getValues={getValues}
            setValue={setValue}
            disabled={!editing}
            isLoading={editing && fetchingPostConfigFields}
            publicForm
          />
        </Box>
      )}

      <DocumentationDrawer
        open={docsDrawerOpen}
        onClose={() => setDocsDrawerOpen(false)}
        articleId={articleId}
        fallbackTitle={selectedIntegration?.name ?? 'Documentation'}
      />
    </Paper>
  );
};

export default IntegrationDetailForm;
